/**
 * Sign in — DESIGN.md §9.1.
 *
 * Google is the primary path and is offered only when the server says it is
 * configured (`GET /auth/config`), so an unprovisioned OAuth client shows no
 * dead button.
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, http } from '@/lib/api';
import { useLogin } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

const searchSchema = z.object({
  returnTo: z.string().optional(),
  /** Set by the Google callback when the round trip failed (§9.1). */
  error: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  component: LoginPage,
});

function LoginPage(): JSX.Element {
  const { returnTo, error: redirectError } = Route.useSearch();
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const authConfig = useQuery({
    queryKey: ['auth', 'config'],
    queryFn: () => http.get<{ google: boolean; password: boolean }>('/auth/config'),
    staleTime: Infinity,
  });

  const errorMessage =
    login.error instanceof ApiError ? login.error.message : (redirectError ?? null);

  return (
    <div className="bg-muted/40 grid min-h-dvh place-items-center p-4">
      <div className="bg-card w-full max-w-sm rounded-xl border p-6 shadow-sm">
        <div className="mb-6 text-center">
          <div className="bg-primary text-primary-foreground mx-auto mb-3 grid size-10 place-items-center rounded-lg text-lg font-bold">
            S
          </div>
          <h1 className="text-lg font-semibold tracking-tight">Sign in to Slate</h1>
          <p className="text-muted-foreground mt-1 text-sm">Touchline Connect</p>
        </div>

        {errorMessage ? (
          <p
            role="alert"
            className="border-destructive/30 bg-destructive/10 text-destructive mb-4 rounded-md border px-3 py-2 text-sm"
          >
            {errorMessage}
          </p>
        ) : null}

        {authConfig.data?.google ? (
          <>
            <Button variant="outline" className="w-full" asChild>
              <a
                href={`/api/v1/auth/google${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
              >
                Continue with Google
              </a>
            </Button>
            <div className="text-muted-foreground my-4 flex items-center gap-3 text-xs uppercase tracking-wide">
              <span className="bg-border h-px flex-1" />
              or
              <span className="bg-border h-px flex-1" />
            </div>
          </>
        ) : null}

        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            // No navigation here: the root route's AuthGate owns redirection
            // and honours `returnTo` once the session exists.
            login.mutate({ email, password });
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={Boolean(errorMessage)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={Boolean(errorMessage)}
            />
          </div>
          <Button type="submit" className="w-full" disabled={login.isPending}>
            {login.isPending ? <Spinner /> : null}
            Sign in
          </Button>
        </form>

        <p className="text-muted-foreground mt-5 text-center text-xs">
          No account? Slate accounts are created by an administrator.
        </p>
      </div>
    </div>
  );
}
